/**
 * noZoom: a tap on a box never zooms the page (founder, 4 Oct 2026: *"it
 * should never need us to resize the screen with 2 fingers pinch back"*).
 *
 * An iPhone zooms in on any text box whose writing is under 16px, and does not
 * zoom back out when the box is left. Karr's body text is 15px and most boxes
 * are 14px, so nearly every tap on a field, and every drawer that focuses one
 * as it opens, left the page enlarged.
 *
 * Three layers, so no single miss brings it back:
 *   1. globals.css ("noZoom"): on a touch screen every typing box is 16px,
 *      unless its class asks for something bigger. All phones, all browsers.
 *   2. Here: on an iPhone or iPad, `maximum-scale=1` on the viewport. Safari
 *      reads it only to stop the zoom on a box; since iOS 10 it ignores it for
 *      a two-finger pinch, so anyone who wants to zoom on purpose still can.
 *      Android is left alone: there it WOULD stop the pinch, and Android does
 *      not zoom on a box in the first place.
 *   3. globals.css: `touch-action: manipulation` on the page stops the
 *      double-tap zoom and keeps the pinch.
 *
 * @since nozoom
 */
import { isIOSDevice } from './installed';

/** The viewport line with the iPhone's focus zoom switched off. Pure, for the test. */
export function noFocusZoomViewport(content: string): string {
    if (/maximum-scale\s*=/i.test(content)) return content;
    return `${content.trim().replace(/,\s*$/, '')}, maximum-scale=1`;
}

/** Call once at start, before anything can take focus. */
export function stopFocusZoom(): void {
    if (typeof document === 'undefined' || !isIOSDevice()) return;
    try {
        const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
        if (meta) meta.content = noFocusZoomViewport(meta.content);
    } catch { /* leave the page as it is */ }
}
